import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ActionButtonState } from 'components/ActionButton';
import Button from 'components/Button';
import {
	LoadingSkeleton,
	LoadingSkeletonItem,
} from 'components/LoadingSkeleton';
import RichText from 'components/RichText';
import Text from 'components/Text';
import { usePriceContext } from 'contexts';
import type { ValidationError } from 'errors';
import { useEffectOnce } from 'hooks';
import { useProfilingGTM, usePromotionGTM } from 'hooks/gtm';
import type {
	CreateCustomizationRequest,
	CustomizationPlacement,
	Option,
	Placement,
	StoredFileResponse,
} from 'models/api';
import type { ProductPrice } from 'models/price';
import type { PrintOnClothes, VariantId } from 'models/product';
import { ignorePromiseRejection, is } from 'utils/helpers';

import type { ExistingPlacement } from '../hooks';
import {
	useProductPrintContext,
	useStockContext,
} from '../ProductDetailsContext';

import PrintPlacement from './PrintPlacement';
import ProductPrintPopover from './ProductPrintPopover';

interface Props {
	addPrintPlacementButtonState: ActionButtonState;
	addPrintPlacementErrors: ValidationError | undefined;
	className?: string;
	existingPrintPlacements: ExistingPlacement[] | undefined;
	fileUploadButtonState: ActionButtonState;
	onAddPrintPlacementClick: (newPrint: CreateCustomizationRequest) => void;
	onRemovePrintClick: (id: string) => void;
	onRemovePrintPlacementClick: (identifier: string) => void;
	onReusePrintClick: (id: string) => void;
	onSelectExistingPrintPlacementClick: (
		newPrint: CreateCustomizationRequest,
	) => void;
	onUpLoadPrint: (file: File) => void;
	placements: Placement[] | undefined;
	print: StoredFileResponse | undefined;
	printOnClothes: PrintOnClothes;
	printPlacements: CustomizationPlacement[] | undefined;
	printUploadErrors: string[] | undefined;
	productId: VariantId;
	storedPrints: StoredFileResponse[] | undefined;
	total: ProductPrice | undefined;
}

export function ProductPrint({
	addPrintPlacementButtonState,
	addPrintPlacementErrors: currentAddPrintPlacementErrors,
	className,
	existingPrintPlacements: existingPrintPlacementsProp,
	fileUploadButtonState,
	onAddPrintPlacementClick,
	onRemovePrintClick,
	onRemovePrintPlacementClick,
	onReusePrintClick,
	onSelectExistingPrintPlacementClick,
	onUpLoadPrint,
	placements,
	print,
	printOnClothes,
	printPlacements,
	printUploadErrors: currentPrintUploadErrors,
	productId,
	storedPrints,
	total,
}: Props) {
	const existingPrintPlacements = useMemo(
		() =>
			existingPrintPlacementsProp?.filter(
				(exitingPrint) =>
					!printPlacements?.some(
						(printPlacement) => printPlacement.id === exitingPrint.id,
					),
			),
		[existingPrintPlacementsProp, printPlacements],
	);

	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const [isFullyClosed, setIsFullyClosed] = useState(false);
	const [currentPageIndex, setCurrentPageIndex] = useState(
		is.arrayWithLength(existingPrintPlacements) ? 3 : 0,
	);
	const { usePriceExcVat } = usePriceContext();
	const [selectedSize, setSelectedSize] = useState<Option>();
	const [selectedPlacement, setSelectedPlacement] = useState<Placement>();

	const [printUploadErrors, setPrintUploadErrors] = useState<string[]>();
	const [addPrintPlacementErrors, setAddPrintPlacementErrors] = useState<
		ValidationError | undefined
	>();

	const reset = useCallback(() => {
		setSelectedPlacement(undefined);
		setSelectedSize(undefined);
		onRemovePrintClick('');
		setPrintUploadErrors(undefined);
		setAddPrintPlacementErrors(undefined);
		// TODO: set on open instead to avoid flicker, if that is the issue?
		setCurrentPageIndex(is.arrayWithLength(existingPrintPlacements) ? 3 : 0);
	}, [existingPrintPlacements, onRemovePrintClick]);

	// Sometimes the reset is run before the existingPrintPlacements array
	// is updated, leading to the currentPageIndex not being correct
	// when the popover is opened, add this extra use effect to deal with that
	useEffect(() => {
		if (isFullyClosed) {
			setCurrentPageIndex(is.arrayWithLength(existingPrintPlacements) ? 3 : 0);
		}
	}, [existingPrintPlacements, isFullyClosed]);

	useEffect(() => {
		setPrintUploadErrors(currentPrintUploadErrors);
	}, [currentPrintUploadErrors]);

	useEffect(() => {
		setAddPrintPlacementErrors(currentAddPrintPlacementErrors);
	}, [currentAddPrintPlacementErrors]);

	useEffect(() => {
		if (addPrintPlacementButtonState === 'success') {
			setIsPopoverOpen(false);
		}
	}, [addPrintPlacementButtonState]);

	const { sendSelectPromotionEvent, sendViewPromotionEvent } =
		usePromotionGTM();
	const {
		sendProfilingCompleteEvent,
		sendProfilingPlacementEvent,
		sendProfilingSizeEvent,
		sendProfilingStartEvent,
		sendProfilingUploadEvent,
	} = useProfilingGTM();

	useEffectOnce(() => {
		sendViewPromotionEvent({
			id: 'profiling',
			name: `Profiling: Clothing ${productId}`,
		});
	});

	const {
		addedPrintsLabel,
		addPrintDescription,
		addPrintHeading,
		addPrintPrimaryButton,
		addPrintSecondaryButton,
		placementDisabled,
		placementHeading,
		popoverCancel,
		popoverName,
		printFileInputHelpText,
		removePrintButton,
		reuseDescription,
		reuseHeading,
		reuseLabel,
		reusePrintButton,
		reuseSubmitButton,
		sizeHeading,
		totalPriceLabel,
		uploadDescription,
		uploadHeading,
		uploadPriceLabel,
		uploadPrintButton,
		uploadRemoveButton,
		uploadReuseButton,
		uploadReuseLabel,
		uploadSubmitButton,
		uploadWarning,
	} = printOnClothes;

	return (
		<div className={className}>
			<Text as="p" field={addPrintHeading} className="font-bold" />
			<RichText field={addPrintDescription} />

			{is.arrayWithLength(printPlacements) && (
				<>
					<Text
						as="p"
						text={addedPrintsLabel?.value}
						className="mt-8 font-bold"
					/>
					<ul
						className="mt-4 flex flex-col gap-2"
						data-cy="print-placements-list"
					>
						{printPlacements.map((printPlacement) => (
							<PrintPlacement
								key={printPlacement.id}
								placement={printPlacement.title || ''}
								logotypeName={
									printPlacement.printImageFilename?.originalFileName
								}
								size={printPlacement.option?.text}
								price={
									usePriceExcVat
										? printPlacement.option?.price?.priceExcVat
										: printPlacement.option?.price?.priceIncVat
								}
								onClick={() => {
									onRemovePrintPlacementClick(printPlacement.id || '');
									reset();
								}}
								buttonText={removePrintButton?.value}
							/>
						))}
					</ul>
				</>
			)}
			<Button
				variant="text"
				leadingIcon="add-print"
				className="mt-4"
				aria-haspopup="dialog"
				data-cy="add-print-button"
				onClick={() => {
					setIsPopoverOpen(true);
					setIsFullyClosed(false);
					sendSelectPromotionEvent({
						id: 'profiling',
						name: `Profiling: Clothing ${productId}`,
					});
					sendProfilingStartEvent(productId);
				}}
			>
				{is.arrayWithLength(printPlacements)
					? addPrintPrimaryButton?.value
					: addPrintSecondaryButton?.value}
			</Button>
			{total && (
				<Text as="p" className="mt-4">
					{totalPriceLabel?.value}{' '}
					<span className="font-bold" data-cy="print-total-price">
						{/* Total is typed as a price object but the displayValue and
						displaySymbol are NOT usable like normal - here the displayValue
						is a math expression that can contain the displaySymbol even
						if the displaySymbol also has a value. Using it like normal
						will result in incorrect formatting. */}
						{usePriceExcVat
							? total.priceExcVat?.displayValue
							: total.priceIncVat?.displayValue}
					</span>
				</Text>
			)}
			<ProductPrintPopover
				existingPrintPlacements={existingPrintPlacements}
				onCreateNewPrintPlacementClick={() => {
					setCurrentPageIndex(0);
					setAddPrintPlacementErrors(undefined);
				}}
				onBackClick={() => {
					setCurrentPageIndex((current) => current - 1);
				}}
				onSelectExistingPrintPlacementClick={
					onSelectExistingPrintPlacementClick
				}
				isOpen={isPopoverOpen}
				currentPageIndex={currentPageIndex}
				onAddPrintPlacementClick={() => {
					if (selectedPlacement && selectedSize && print?.fileName) {
						onAddPrintPlacementClick({
							placementId: selectedPlacement.id,
							printImageFilename: print.fileName,
							optionKey: selectedSize.key,
						});
						sendProfilingCompleteEvent(productId);
					}
				}}
				onClose={() => {
					setIsPopoverOpen(false);
				}}
				onCloseDone={() => {
					reset();
					setIsFullyClosed(true);
				}}
				onPlacementClick={(placement) => {
					setSelectedPlacement(placement);
					setCurrentPageIndex((current) => current + 1);
					sendProfilingPlacementEvent(productId);
				}}
				onPrintUploadAbortClick={() => {
					setIsPopoverOpen(false);
				}}
				onRemovePrintClick={onRemovePrintClick}
				onSizeClick={(sizeOption) => {
					setSelectedSize(sizeOption);
					setCurrentPageIndex((current) => current + 1);
					sendProfilingSizeEvent(productId);
				}}
				onUpLoadPrint={(file) => {
					onUpLoadPrint(file);
					sendProfilingUploadEvent(productId);
				}}
				placements={placements?.map((placement) => ({
					...placement,
					isSelected: Boolean(
						printPlacements?.some(
							(printPlacement) => printPlacement.id === placement.id,
						),
					),
				}))}
				sizes={selectedPlacement?.options}
				print={print}
				storedPrints={storedPrints}
				onReusePrintClick={onReusePrintClick}
				fileUploadButtonState={fileUploadButtonState}
				addPrintPlacementButtonState={addPrintPlacementButtonState}
				addNewPrintPlacementButtonText={reuseSubmitButton?.value}
				addPrintPlacementButtonText={uploadSubmitButton?.value}
				existingPrintPlacementsHeading={reuseHeading?.value}
				existingPrintPlacementsListHeading={reuseLabel?.value}
				existingPrintPlacementsDescription={reuseDescription?.value}
				placementDisabledText={placementDisabled?.value}
				placementHeading={placementHeading?.value}
				// Very unlikely to be undefined in practice
				popoverFallbackHeading={popoverName?.value || 'Print'}
				selectedPlacementPrice={selectedSize?.price}
				printUploadAbortButtonText={popoverCancel?.value}
				printUploadDescription={uploadDescription?.value}
				printUploadHeading={uploadHeading?.value}
				printUploadInfoText={uploadWarning?.value}
				printUploadPriceLabel={uploadPriceLabel?.value}
				removeUploadedPrintButtonText={uploadRemoveButton?.value}
				reusePrintButtonText={uploadReuseButton?.value}
				reuseUploadedPrintText={uploadReuseLabel?.value}
				selectExistingPrintPlacementButtonText={reusePrintButton?.value}
				sizeHeading={sizeHeading?.value}
				uploadButtonText={uploadPrintButton?.value}
				printFileInputHelpText={printFileInputHelpText?.value}
				printUploadErrors={printUploadErrors}
				addPrintPlacementErrors={addPrintPlacementErrors}
			/>
		</div>
	);
}

ProductPrint.displayName = 'ProductPrint';

interface ProductPrintWrapperProps {
	className?: string;
	printOnClothes: PrintOnClothes;
	productId: VariantId;
}

export default function ProductPrintWrapper({
	className,
	printOnClothes,
	productId,
}: ProductPrintWrapperProps) {
	const { isLoadingMainVariantStock, isPurchasable } = useStockContext();
	const {
		addPrintPlacement,
		addPrintPlacementButtonState,
		addPrintPlacementErrors,
		existingPrintPlacements,
		isInitialLoadingProductPrint,
		placements,
		print,
		printPlacements,
		printUploadButtonState,
		printUploadErrors,
		productPrintHasError,
		removePrint,
		removePrintPlacement,
		reusePrint,
		storedPrints,
		totalCost,
		uploadPrint,
	} = useProductPrintContext();

	if (!isPurchasable) {
		return null;
	}

	if (isInitialLoadingProductPrint || isLoadingMainVariantStock) {
		return (
			<LoadingSkeleton className={className}>
				{/* Text, RichText and Button from above component */}
				<LoadingSkeletonItem height="text" width="10rem" />
				<LoadingSkeletonItem height="text" lines={2} className="mt-1" />
				<LoadingSkeletonItem height="1.5rem" width="10rem" className="mt-5.5" />
			</LoadingSkeleton>
		);
	}
	if (productPrintHasError) {
		return null;
	}

	// If we don't have any placements there isn't much use showing anything.
	// Checking for placements also deals with the possibility that there might
	// be a diff between the product saying it can be customized but the API
	// saying there isn't any customization for it.
	if (!placements) {
		return null;
	}

	return (
		<ProductPrint
			className={className}
			onRemovePrintPlacementClick={removePrintPlacement}
			printPlacements={printPlacements}
			onAddPrintPlacementClick={addPrintPlacement}
			onSelectExistingPrintPlacementClick={addPrintPlacement}
			print={print}
			placements={placements}
			onUpLoadPrint={(file) => {
				ignorePromiseRejection(uploadPrint(file));
			}}
			existingPrintPlacements={existingPrintPlacements}
			total={totalCost}
			storedPrints={storedPrints}
			addPrintPlacementButtonState={addPrintPlacementButtonState}
			fileUploadButtonState={printUploadButtonState}
			onReusePrintClick={reusePrint}
			printOnClothes={printOnClothes}
			onRemovePrintClick={removePrint}
			printUploadErrors={printUploadErrors}
			addPrintPlacementErrors={addPrintPlacementErrors}
			productId={productId}
		/>
	);
}
ProductPrintWrapper.displayName = 'ProductPrintWrapper';
